import React, { createContext, useContext, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Shared "Hide / Show calcs" mechanism used by the builder panels
 * (Re-roof, Repairs, Restoration, Maintenance, Purlin Calculator).
 *
 * Wrap a panel in <CalcShadowProvider> and drop <CalcShadowToggle/>
 * into the header. Then use <CalcShadow>...</CalcShadow> wherever you
 * want a shadowed italic formula line to appear under a total — the
 * provider's `show` state strips them all in one click.
 *
 * The pattern keeps the dataflow visible: every numeric total carries
 * a one-line breakdown of how it was derived from the spec inputs.
 */
const Ctx = createContext({ show: true, setShow: () => {} });

export function CalcShadowProvider({ children, defaultShow = true }) {
  const [show, setShow] = useState(defaultShow);
  return <Ctx.Provider value={{ show, setShow }}>{children}</Ctx.Provider>;
}

export function CalcShadowToggle({ testid = 'calc-shadow-toggle', className = '' }) {
  const { show, setShow } = useContext(Ctx);
  return (
    <button
      data-testid={testid}
      onClick={() => setShow((v) => !v)}
      className={`bg-white border border-slate-300 text-slate-700 text-[11px] font-semibold rounded px-2 py-1 hover:bg-slate-50 inline-flex items-center gap-1 ${className}`}
      title="Show or hide the calculation breakdown shadow lines"
    >
      {show ? <EyeOff size={11} /> : <Eye size={11} />} {show ? 'Hide calcs' : 'Show calcs'}
    </button>
  );
}

export function CalcShadow({ children, testid, tone = 'slate' }) {
  const { show } = useContext(Ctx);
  if (!show) return null;
  const toneCls =
    tone === 'emerald' ? 'text-emerald-700/70' :
    tone === 'sky'     ? 'text-sky-700/70'     :
    tone === 'amber'   ? 'text-amber-700/80'   :
    tone === 'violet'  ? 'text-violet-700/70'  :
                         'text-slate-500/80';
  return (
    <div
      data-testid={testid}
      className={`mt-0.5 text-[10px] font-mono-num italic leading-snug ${toneCls}`}
    >
      {children}
    </div>
  );
}
